import {
  DESCRIPTION_MAX_LENGTH,
  NAME_MAX_LENGTH,
  NAME_MIN_LENGTH,
  type CreateProjectPayload,
  type UpdateProjectPayload,
} from '@atlas/api-contract';
import { z } from 'zod';

import { isApiError } from '@/lib/api/errors';

/**
 * The project form, mirroring the server's `CreateProjectDto`.
 *
 * Both sides read their bounds from `@atlas/api-contract`, so the client cannot
 * start accepting a name the server will reject. The behaviour is mirrored too:
 * trim first, then measure, and fold an empty description to `null` rather than
 * storing two spellings of "no description".
 *
 * Uniqueness is deliberately *not* mirrored. It cannot be: only the database
 * knows, and any check here would be a race. The 409 is the answer, surfaced on
 * the field that caused it.
 */
export const projectFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(NAME_MIN_LENGTH, 'Give the project a name.')
    .max(
      NAME_MAX_LENGTH,
      `Use ${String(NAME_MAX_LENGTH)} characters or fewer.`,
    ),
  description: z
    .string()
    .trim()
    .max(
      DESCRIPTION_MAX_LENGTH,
      `Use ${String(DESCRIPTION_MAX_LENGTH)} characters or fewer.`,
    ),
});

export type ProjectFormValues = z.infer<typeof projectFormSchema>;

/** Empty form state, so create always opens clean. */
export const EMPTY_PROJECT_FORM: ProjectFormValues = {
  name: '',
  description: '',
};

/** An absent description is `null`, never `''` (matching the server's DTO). */
const toDescription = (description: string): string | null =>
  description.trim() === '' ? null : description.trim();

export const toCreateProjectPayload = (
  values: ProjectFormValues,
): CreateProjectPayload => ({
  name: values.name.trim(),
  description: toDescription(values.description),
});

export const toUpdateProjectPayload = (
  values: ProjectFormValues,
): UpdateProjectPayload => ({
  name: values.name.trim(),
  description: toDescription(values.description),
});

/**
 * What the name field says when the server rejects the name as taken.
 *
 * Written here rather than taken from the response: the API's message is
 * generated from the constraint ("A record with this name already exists.") and
 * names a record, not a project.
 */
export const DUPLICATE_PROJECT_NAME_MESSAGE =
  'A project with this name already exists. Pick a different one.';

/** Whether a failure is the unique-name conflict, and belongs on the field. */
export const isDuplicateNameError = (error: unknown): boolean =>
  isApiError(error) && error.status === 409;
